import { useCallback, useEffect, useState } from "react";
import { PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WheelSvg } from "@/components/Wheel";
import { Confetti, GiftBoxes, ScratchCard } from "@/components/Games";
import { api, errMsg } from "@/lib/api";

const itDate = (d) => (d ? d.split("-").reverse().join("/") : "");
const COPY = {
  ruota: ["Gira la ruota e scopri il regalo che abbiamo pensato per il tuo evento.", "Spin the wheel and discover the gift we've chosen for your event."],
  gratta: ["Gratta la card dorata con il dito e scopri il tuo regalo.", "Scratch the golden card with your finger to reveal your gift."],
  regali: ["Scegli uno dei tre pacchi: dentro c'è un regalo per il tuo evento.", "Pick one of the three boxes: there's a gift for your event inside."],
};

function Won({ win, t }) {
  return (
    <div className="space-y-3 py-4 fade-up" data-testid="spin-won">
      <Confetti />
      <PartyPopper size={48} className="mx-auto text-[#C9A96A]" />
      <div className="text-xs uppercase tracking-[0.3em] text-[#34B3B0]">{win.ripetuto ? t("Il tuo premio", "Your prize") : t("Hai vinto", "You won")}</div>
      <div className="font-serif-it text-4xl font-semibold text-[#0B6E8F]" data-testid="spin-prize">{win.nome}</div>
      <p className="text-sm text-[#47606B] max-w-sm mx-auto">{win.ripetuto ? t("Hai già giocato con questo numero: il premio resta quello vinto.", "You already played with this number: your prize stays the same.") + " " : ""}{t(`Il premio è valido se confermi il tuo evento firmando il contratto entro il ${itDate(win.scade)}. Mostra questo messaggio o citalo al nostro staff.`, `The prize is valid if you confirm your event by signing the contract by ${itDate(win.scade)}. Just mention it to our staff.`)}</p>
    </div>
  );
}

function WheelPlay({ list, play, t }) {
  const [rot, setRot] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const go = async () => {
    setSpinning(true);
    const r = await play(5400);
    if (!r) return setSpinning(false);
    const n = r.premi.length || 1;
    const center = r.index >= 0 ? r.index * (360 / n) + 180 / n : 0;
    setRot(360 * 6 + (360 - center) + (Math.random() * 0.5 - 0.25) * (360 / n));
  };
  return (
    <>
      <div className="scale-[0.88] sm:scale-100 -my-4 sm:my-0"><WheelSvg premi={list} rot={rot} spinning={spinning} /></div>
      <Button className="rounded-full px-10 h-12 text-base bg-[#E0533D] hover:bg-[#c94430]" disabled={spinning} onClick={go} data-testid="spin-button">{spinning ? t("Gira…", "Spinning…") : t("GIRA LA RUOTA", "SPIN THE WHEEL")}</Button>
    </>
  );
}

export function SpinGame({ token, tipo, t }) {
  const [cfg, setCfg] = useState(null);
  const [win, setWin] = useState(null);
  const [shown, setShown] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/public/wheel", { params: { tipo } }).then((r) => setCfg(r.data)).catch(() => setCfg({ gioco: "ruota", premi: [] }));
  }, [tipo]);
  const play = useCallback(async (revealAfter) => {
    setErr("");
    try {
      const r = (await api.post("/public/wheel/spin", { token })).data;
      setWin(r);
      if (revealAfter) setTimeout(() => setShown(true), revealAfter);
      return r;
    } catch (e) {
      setErr(errMsg(e));
      return null;
    }
  }, [token]);
  const gioco = cfg?.gioco || "ruota";
  useEffect(() => {
    if (cfg && gioco === "gratta") {
      play(0);
    }
  }, [cfg, gioco, play]);
  if (!cfg) return null;
  return (
    <div className="text-center space-y-4 py-2" data-testid="spin-game" data-game={gioco}>
      {shown ? <Won win={win} t={t} /> : (
        <>
          <h2 className="font-serif-it text-3xl font-semibold">{t("La sorpresa è qui!", "Here's your surprise!")}</h2>
          <p className="text-sm text-[#47606B]">{t(...COPY[gioco])}</p>
          {gioco === "gratta" && <ScratchCard prize={win} onDone={() => setShown(true)} t={t} />}
          {gioco === "regali" && <GiftBoxes premi={win?.premi || cfg.premi} onPick={() => play(4600)} t={t} />}
          {gioco === "ruota" && <WheelPlay list={win?.premi || cfg.premi} play={play} t={t} />}
          {err && <div className="text-sm text-[#991B1B]" data-testid="spin-error">{err}</div>}
        </>
      )}
    </div>
  );
}
